import { useState, useCallback } from "react";
import { useLingui } from "@lingui/react/macro";
import { useAuth } from "@/hooks/useAuth";
import {
  MoreHorizontal,
  PauseCircle,
  CheckCircle,
  PlayCircle,
  Search,
  ArrowUpDown,
  SlidersHorizontal,
  LayoutList,
  LayoutGrid,
  ChefHat,
  Loader2,
  Eye,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { StatusPill as UiStatusPill } from "@/components/ui/status-pill";
import { WorkingTablePagination } from "@/components/data-table/working-table-pagination";
import { useLiveElapsedSeconds } from "@/lib/elapsed";
import { ProgressCircle, TimerPill } from "./CookTimer";
import { useCookingSessions } from "@/hooks/cooking-sessions/queries";
import { AssigneeBadge } from "./AssigneeBadge";
import { AssigneeFilterOptions } from "./AssigneeFilterOptions";
import { type AssigneeFilter, assigneeFilterParams } from "./assignee-filter";
import { useBulkActionCookingSessions, useUpdateCookingSession, useDeleteCookingSession } from "@/hooks/cooking-sessions/mutations";
import { useSessionBatchScale } from "@/hooks/cooking-sessions/useSessionBatchScale";
import { useRecipe } from "@/hooks/recipes/queries";
import { CookModeDialog } from "@/components/recipes/dialogs/CookModeDialog";
import { CookingSessionDetailsPanel } from "./CookingSessionDetailsPanel";
import { BulkActionsBar } from "./BulkActionsBar";
import { useSelectionSlot } from "./multi-view/selection-store";
import { BulkActionConfirmBar } from "./BulkActionConfirmBar";
import { DeleteConfirmDialog } from "./DeleteConfirmDialog";
import type { BulkSessionAction, CookingSession, CookingSessionSource, CookingSessionStatus } from "@/types/cooking-session";
import type { Recipe } from "@/types/recipe";

const PAGE_SIZE = 5;

// ---------------------------------------------------------------------------
// Status pill
// ---------------------------------------------------------------------------

type DisplayStatus = "Cooking" | "Paused" | "Late" | "Done";

function resolveDisplayStatus(session: CookingSession): DisplayStatus {
  if (session.status === "COMPLETED") return "Done";
  if (session.isLate) return "Late";
  if (session.status === "PAUSED") return "Paused";
  return "Cooking";
}

const STATUS_TO_PILL = {
  Cooking: "cooking",
  Paused: "paused",
  Late: "late",
  Done: "done",
} as const;

function StatusPill({ status }: { status: DisplayStatus }) {
  const { t } = useLingui();
  const LABELS: Record<DisplayStatus, string> = {
    Cooking: t`Cooking`,
    Paused:  t`Paused`,
    Late:    t`Late`,
    Done:    t`Done`,
  };
  return (
    <UiStatusPill variant="solid" size="lg" status={STATUS_TO_PILL[status]}>
      {LABELS[status]}
    </UiStatusPill>
  );
}

// ---------------------------------------------------------------------------
// Column layout
// ---------------------------------------------------------------------------

// Three tiers, switched by the width of the table's own container (`@container` on
// the list wrapper) so the sidebar being shown or hidden is accounted for (YC-32).
// Wide (container >= 73.5rem / 1176px, the previous minimum width) keeps the
// original widths; compact (56rem to 73.5rem, iPad landscape) keeps all nine
// columns, narrower; narrow (below 56rem, iPad portrait) drops Allergens, Started
// and Finished as columns and shows them under the recipe name instead.
const COLS = {
  checkbox:  "w-10 @min-[73.5rem]:w-[50px] shrink-0",
  recipe:    "flex-1 min-w-28 @min-[56rem]:min-w-24 @min-[73.5rem]:min-w-[200px]",
  allergens: "hidden @min-[56rem]:block w-18 @min-[73.5rem]:w-[140px] shrink-0",
  chef:      "w-30 @min-[73.5rem]:w-[150px] shrink-0",
  started:   "hidden @min-[56rem]:block w-19 @min-[73.5rem]:w-[100px] shrink-0",
  finished:  "hidden @min-[56rem]:block w-19 @min-[73.5rem]:w-[100px] shrink-0",
  timer:     "w-[130px] shrink-0",
  status:    "w-24 @min-[73.5rem]:w-[110px] shrink-0",
  actions:   "w-36 @min-[73.5rem]:w-[150px] shrink-0",
};

function TableHeader({ allSelected, someSelected, onToggleAll }: {
  allSelected: boolean; someSelected: boolean; onToggleAll: (v: boolean) => void;
}) {
  const { t } = useLingui();
  const HEADER_COLS = [
    { id: "recipe",    label: t`Recipe`,    col: COLS.recipe },
    { id: "allergens", label: t`Allergens`, col: COLS.allergens },
    { id: "chef",      label: t`Chef`,      col: COLS.chef },
    { id: "started",   label: t`Started`,   col: COLS.started },
    { id: "finished",  label: t`Finished`,  col: COLS.finished },
    { id: "timer",     label: t`Timer`,     col: COLS.timer },
    { id: "status",    label: t`Status`,    col: COLS.status },
    { id: "actions",   label: t`Actions`,   col: COLS.actions },
  ];
  return (
    <div className="flex items-center w-full px-3 border-b border-(--color-border) mb-1">
      <div className={cn(COLS.checkbox, "py-3")}>
        <Checkbox checked={allSelected || (someSelected && "indeterminate")} onCheckedChange={(v) => onToggleAll(v === true)} aria-label={t`Select all`} />
      </div>
      {HEADER_COLS.map(({ id, label, col }) => (
        <div key={id} className={cn(col, "py-3 px-2 text-sm font-medium text-(--color-text-secondary)")}>{label}</div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cook-mode loader — fetches recipe on demand when row is clicked
// ---------------------------------------------------------------------------

function CookModeLoader({ recipeId, sessionId, batchScale, onClose }: { recipeId: number; sessionId: number; batchScale: number; onClose: () => void }) {
  const { data: recipe } = useRecipe(recipeId);
  const updateSession = useUpdateCookingSession();
  // The batch size belongs to the session: it reopens at the size last chosen.
  const saveBatchScale = useSessionBatchScale(sessionId);
  if (!recipe) return null;
  async function handleFinish() {
    await updateSession.mutateAsync({ id: sessionId, data: { status: "COMPLETED" } });
  }
  return (
    <CookModeDialog
      recipe={recipe as Recipe}
      open
      onOpenChange={(v) => { if (!v) onClose(); }}
      onFinish={handleFinish}
      initialScale={batchScale}
      onScaleChange={saveBatchScale}
      cookingSessionId={sessionId}
    />
  );
}

// ---------------------------------------------------------------------------
// Data row
// ---------------------------------------------------------------------------

function CookingRow({
  row, selected, onSelect, onAction, onDelete, onCookMode, onViewDetails, canCook, canUpdate, canDelete,
}: {
  row: CookingSession;
  selected: boolean;
  onSelect: (v: boolean) => void;
  onAction: (id: number, status: CookingSessionStatus) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onCookMode: (id: number) => void;
  onViewDetails: (id: number) => void;
  canCook: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}) {
  const { t } = useLingui();
  const displayStatus = resolveDisplayStatus(row);
  const elapsedSeconds = useLiveElapsedSeconds(row);
  const [pendingAction, setPendingAction] = useState<"resume" | "complete" | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const initials = row.chefName
    ? row.chefName.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()
    : "?";

  const startedLabel = row.startedAt
    ? new Date(row.startedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "—";

  // Narrow tier only: the columns that are dropped there, as one sub-line under the recipe name.
  const compactDetails = [
    row.orderAllergens.length > 0 ? `${t`Allergens:`} ${row.orderAllergens.join(', ')}` : null,
    row.startedAt ? `${t`Started`} ${startedLabel}` : null,
    row.completedAt
      ? `${t`Finished`} ${new Date(row.completedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
      : null,
  ].filter(Boolean).join(" · ");

  return (
    <>
    <div
      className="flex items-center w-full bg-(--color-surface-muted) rounded-xl px-3 mb-1.5 cursor-pointer hover:bg-(--color-surface-accent)/60 transition-colors"
      onClick={() => onViewDetails(row.id)}
      role="button"
      tabIndex={0}
      aria-label={t`View details for ${row.recipeName}`}
      onKeyDown={(e) => {
        if (e.key === "Enter") { onViewDetails(row.id); }
        if (e.key === " ") { e.preventDefault(); onViewDetails(row.id); }
      }}
    >
      {/* Checkbox */}
      <div className={cn(COLS.checkbox, "py-4 flex items-center")} onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={selected} onCheckedChange={(v) => onSelect(v === true)} aria-label={t`Select row`} className="bg-(--color-surface-content) border-(--color-border)" />
      </div>

      {/* Recipe */}
      <div className={cn(COLS.recipe, "py-3 px-2 flex items-center gap-2")}>
        {/* The ring repeats the Timer column, so the compact tier drops it to give the name room */}
        <span className="hidden shrink-0 @min-[73.5rem]:flex">
          <ProgressCircle elapsedSeconds={elapsedSeconds} target={row.targetMinutes} />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-medium text-(--color-text-primary) truncate @max-[73.5rem]:whitespace-normal @max-[73.5rem]:line-clamp-2">{row.recipeName}</p>
          <p className="text-xs text-(--color-text-secondary)">{t`Target:`} {row.targetMinutes}m</p>
          {compactDetails && (
            <p className="text-xs text-(--color-text-secondary) break-words @min-[56rem]:hidden">{compactDetails}</p>
          )}
        </div>
      </div>

      {/* Allergens */}
      <div className={cn(COLS.allergens, "py-4 px-2")}>
        <span className="text-sm font-medium text-(--color-text-primary) break-words">
          {row.orderAllergens.length > 0 ? row.orderAllergens.join(', ') : "—"}
        </span>
      </div>

      {/* Chef */}
      <div className={cn(COLS.chef, "py-3 px-2 flex items-center gap-2")}>
        {row.chefName || row.stationName ? (
          <AssigneeBadge chefName={row.chefName} stationName={row.stationName} />
        ) : (
          <>
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] bg-(--color-action)/10 text-xs font-semibold text-(--color-action)">
              {initials}
            </div>
            <span className="min-w-0 text-sm font-medium text-(--color-text-primary) break-words">{t`Unassigned`}</span>
          </>
        )}
      </div>

      {/* Started */}
      <div className={cn(COLS.started, "py-4 px-2")}>
        <span className="text-sm font-medium text-(--color-text-primary)">{startedLabel}</span>
      </div>

      {/* Finished */}
      <div className={cn(COLS.finished, "py-4 px-2")}>
        <span className="text-base font-semibold text-(--color-text-secondary)">
          {row.completedAt
            ? new Date(row.completedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
            : "—"}
        </span>
      </div>

      {/* Timer */}
      <div className={cn(COLS.timer, "py-3 px-2")}>
        <TimerPill session={row} elapsedSeconds={elapsedSeconds} />
      </div>

      {/* Status */}
      <div className={cn(COLS.status, "py-3 px-2")}>
        <StatusPill status={displayStatus} />
      </div>

      {/* Actions */}
      {(canCook || canUpdate || canDelete) && (
        <div className={cn(COLS.actions, "py-3 flex items-center gap-1.5")} onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label={t`More options`}
                className="h-11 w-11 shrink-0 rounded-xl border-(--color-border) bg-(--color-surface-content) hover:bg-(--color-surface-muted) [&_svg]:size-5">
                <MoreHorizontal className="text-(--color-text-primary)" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              {canCook && (
                <DropdownMenuItem onClick={() => onCookMode(row.id)}>
                  <Eye className="h-4 w-4" />
                  {t`Open cook mode`}
                </DropdownMenuItem>
              )}
              {canCook && (row.status === "PAUSED" || canUpdate) && <DropdownMenuSeparator />}
              {row.status === "PAUSED" ? (
                canCook && (
                  <DropdownMenuItem
                    onClick={async () => {
                      setPendingAction("resume");
                      try { await onAction(row.id, "COOKING"); onCookMode(row.id); }
                      finally { setPendingAction(null); }
                    }}
                  >
                    <PlayCircle className="h-4 w-4 text-(--color-success-text)" />
                    {t`Resume`}
                  </DropdownMenuItem>
                )
              ) : (
                canUpdate && (
                  <DropdownMenuItem onClick={() => onAction(row.id, "PAUSED")}>
                    <PauseCircle className="h-4 w-4" />
                    {t`Pause`}
                  </DropdownMenuItem>
                )
              )}
              {row.status !== "COMPLETED" && canUpdate && (
                <DropdownMenuItem
                  onClick={async () => {
                    setPendingAction("complete");
                    try { await onAction(row.id, "COMPLETED"); }
                    finally { setPendingAction(null); }
                  }}
                >
                  <CheckCircle className="h-4 w-4 text-(--color-success-text)" />
                  {t`Complete`}
                </DropdownMenuItem>
              )}
              {canDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setDeleteOpen(true)}
                    className="gap-2 text-(--color-danger-text) focus:text-(--color-danger-text)"
                  >
                    <Trash2 className="h-4 w-4 shrink-0" />
                    {t`Delete`}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {row.status !== "COMPLETED" && (
            row.status === "PAUSED" ? (
              canCook && (
                <Button variant="outline" size="icon" aria-label={t`Resume`}
                  disabled={pendingAction !== null}
                  onClick={async () => {
                    setPendingAction("resume");
                    try { await onAction(row.id, "COOKING"); onCookMode(row.id); }
                    finally { setPendingAction(null); }
                  }}
                  className="h-11 w-11 shrink-0 rounded-xl border-(--color-border) bg-(--color-surface-content) hover:bg-(--color-surface-muted) [&_svg]:size-5">
                  {pendingAction === "resume"
                    ? <Loader2 className="animate-spin text-(--color-success-text)" />
                    : <PlayCircle className="text-(--color-success-text)" />}
                </Button>
              )
            ) : (
              canUpdate && (
                <Button variant="outline" size="icon" aria-label={t`Pause`}
                  onClick={() => onAction(row.id, "PAUSED")}
                  className="h-11 w-11 shrink-0 rounded-xl border-(--color-border) bg-(--color-surface-content) hover:bg-(--color-surface-muted) [&_svg]:size-5">
                  <PauseCircle className="text-(--color-text-primary)" />
                </Button>
              )
            )
          )}
          {canUpdate && row.status !== "COMPLETED" && (
            <Button variant="success" size="icon" aria-label={t`Complete`}
              disabled={pendingAction !== null}
              onClick={async () => {
                setPendingAction("complete");
                try { await onAction(row.id, "COMPLETED"); }
                finally { setPendingAction(null); }
              }}
              className="h-11 w-11 shrink-0 rounded-xl [&_svg]:size-5">
              {pendingAction === "complete"
                ? <Loader2 className="animate-spin text-white" />
                : <CheckCircle className="text-white" />}
            </Button>
          )}
        </div>
      )}
    </div>
    <DeleteConfirmDialog
      open={deleteOpen}
      onOpenChange={setDeleteOpen}
      title={t`Delete session`}
      description={t`Delete "${row.recipeName}"? This action cannot be undone.`}
      onConfirm={() => onDelete(row.id)}
    />
  </>
  );
}

// ---------------------------------------------------------------------------
// Card (grid view)
// ---------------------------------------------------------------------------

function CookingNowCard({
  row, selected, onSelect, onAction, onDelete, onCookMode, onViewDetails, canCook, canUpdate, canDelete,
}: {
  row: CookingSession;
  selected: boolean;
  onSelect: (v: boolean) => void;
  onAction: (id: number, status: CookingSessionStatus) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onCookMode: (id: number) => void;
  onViewDetails: (id: number) => void;
  canCook: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}) {
  const { t } = useLingui();
  const displayStatus = resolveDisplayStatus(row);
  const elapsedSeconds = useLiveElapsedSeconds(row);
  const [pendingAction, setPendingAction] = useState<"resume" | "complete" | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  return (
    <>
    <div className="flex flex-col gap-4 bg-(--color-surface-muted) rounded-2xl px-3 pt-3 pb-4">
      {/* Clickable header — opens session details */}
      <div
        className="flex gap-3 items-start cursor-pointer rounded-xl hover:bg-(--color-surface-accent)/60 transition-colors -mx-1 px-1 pt-1"
        onClick={() => onViewDetails(row.id)}
        role="button"
        tabIndex={0}
        aria-label={t`View details for ${row.recipeName}`}
        onKeyDown={(e) => {
          if (e.key === "Enter") onViewDetails(row.id);
          if (e.key === " ") { e.preventDefault(); onViewDetails(row.id); }
        }}
      >
        <ProgressCircle elapsedSeconds={elapsedSeconds} target={row.targetMinutes} large />
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <div className="flex gap-2 items-center flex-wrap">
            <TimerPill session={row} elapsedSeconds={elapsedSeconds} />
            <StatusPill status={displayStatus} />
          </div>
          <p className="text-base font-medium text-(--color-text-primary) truncate">{row.recipeName}</p>
          <p className="text-xs text-(--color-text-secondary)">{t`Target:`} {row.targetMinutes}m</p>
        </div>
        <div className="flex items-start p-2 shrink-0" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={selected}
            onCheckedChange={(v) => onSelect(v === true)}
            aria-label={t`Select`}
            className="bg-(--color-surface-content) border-(--color-border)"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between bg-(--color-surface-content) rounded-xl px-3 py-3">
          <span className="text-sm font-medium text-(--color-text-primary)">{t`Allergens:`}</span>
          <span className="text-sm font-medium text-(--color-text-primary)">
            {row.orderAllergens.length > 0 ? row.orderAllergens.join(', ') : "—"}
          </span>
        </div>
        <div className="flex items-center justify-between bg-(--color-surface-content) rounded-xl px-3 py-3">
          <span className="text-sm font-medium text-(--color-text-primary)">{t`Chef:`}</span>
          {row.chefName || row.stationName ? (
            <div className="max-w-40 min-w-0">
              <AssigneeBadge chefName={row.chefName} stationName={row.stationName} />
            </div>
          ) : (
            <span className="text-sm text-(--color-text-secondary)">{t`Unassigned`}</span>
          )}
        </div>
      </div>

      <div className="border-t border-(--color-border)" />

      {(canCook || canUpdate || canDelete) && (
        <div className="flex items-center gap-1.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label={t`More options`}
                className="h-11 w-11 shrink-0 rounded-xl border-(--color-border) bg-(--color-surface-content) hover:bg-(--color-surface-muted) [&_svg]:size-5">
                <MoreHorizontal className="text-(--color-text-primary)" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              {canCook && (
                <DropdownMenuItem onClick={() => onCookMode(row.id)}>
                  <Eye className="h-4 w-4" />
                  {t`Open cook mode`}
                </DropdownMenuItem>
              )}
              {canCook && (row.status === "PAUSED" || canUpdate) && <DropdownMenuSeparator />}
              {row.status === "PAUSED" ? (
                canCook && (
                  <DropdownMenuItem
                    onClick={async () => {
                      setPendingAction("resume");
                      try { await onAction(row.id, "COOKING"); onCookMode(row.id); }
                      finally { setPendingAction(null); }
                    }}
                  >
                    <PlayCircle className="h-4 w-4 text-(--color-success-text)" />
                    {t`Resume`}
                  </DropdownMenuItem>
                )
              ) : (
                canUpdate && (
                  <DropdownMenuItem onClick={() => onAction(row.id, "PAUSED")}>
                    <PauseCircle className="h-4 w-4" />
                    {t`Pause`}
                  </DropdownMenuItem>
                )
              )}
              {row.status !== "COMPLETED" && canUpdate && (
                <DropdownMenuItem
                  onClick={async () => {
                    setPendingAction("complete");
                    try { await onAction(row.id, "COMPLETED"); }
                    finally { setPendingAction(null); }
                  }}
                >
                  <CheckCircle className="h-4 w-4 text-(--color-success-text)" />
                  {t`Complete`}
                </DropdownMenuItem>
              )}
              {canDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setDeleteOpen(true)}
                    className="gap-2 text-(--color-danger-text) focus:text-(--color-danger-text)"
                  >
                    <Trash2 className="h-4 w-4 shrink-0" />
                    {t`Delete`}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {row.status !== "COMPLETED" && (
            row.status === "PAUSED" ? (
              canCook && (
                <Button variant="outline" size="icon" aria-label={t`Resume`}
                  disabled={pendingAction !== null}
                  onClick={async () => {
                    setPendingAction("resume");
                    try {
                      await onAction(row.id, "COOKING");
                      onCookMode(row.id);
                    } finally {
                      setPendingAction(null);
                    }
                  }}
                  className="h-11 w-11 shrink-0 rounded-xl border-(--color-border) bg-(--color-surface-content) hover:bg-(--color-surface-muted) [&_svg]:size-5">
                  {pendingAction === "resume"
                    ? <Loader2 className="animate-spin text-(--color-success-text)" />
                    : <PlayCircle className="text-(--color-success-text)" />}
                </Button>
              )
            ) : (
              canUpdate && (
                <Button variant="outline" size="icon" aria-label={t`Pause`}
                  onClick={() => onAction(row.id, "PAUSED")}
                  className="h-11 w-11 shrink-0 rounded-xl border-(--color-border) bg-(--color-surface-content) hover:bg-(--color-surface-muted) [&_svg]:size-5">
                  <PauseCircle className="text-(--color-text-primary)" />
                </Button>
              )
            )
          )}
          {canUpdate && row.status !== "COMPLETED" && (
            <Button variant="success" aria-label={t`Complete`}
              disabled={pendingAction !== null}
              onClick={async () => {
                setPendingAction("complete");
                try { await onAction(row.id, "COMPLETED"); }
                finally { setPendingAction(null); }
              }}
              className="h-11 flex-1 rounded-xl [&_svg]:size-5 gap-2">
              {pendingAction === "complete"
                ? <Loader2 className="animate-spin text-white" />
                : <CheckCircle className="text-white" />}
              {pendingAction === "complete" ? t`Finishing...` : t`Complete`}
            </Button>
          )}
        </div>
      )}
    </div>
    <DeleteConfirmDialog
      open={deleteOpen}
      onOpenChange={setDeleteOpen}
      title={t`Delete session`}
      description={t`Delete "${row.recipeName}"? This action cannot be undone.`}
      onConfirm={() => onDelete(row.id)}
    />
    </>
  );
}

// ---------------------------------------------------------------------------
// Filter options
// ---------------------------------------------------------------------------

// No COMPLETED: what is done belongs to the Completed lane (T-1108), and a
// lane the kitchen watches during service should hold only live work.
type StatusFilter = "all" | "COOKING" | "PAUSED" | "late";

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Details panel loader — fetches recipe on demand when a session row is clicked
// ---------------------------------------------------------------------------

function DetailsPanelLoader({ session, onClose, onAction, onCookMode, canCook, canUpdate }: {
  session: CookingSession;
  onClose: () => void;
  onAction: (id: number, status: CookingSessionStatus) => Promise<void>;
  onCookMode?: () => void;
  canCook: boolean;
  canUpdate: boolean;
}) {
  const { data: recipe } = useRecipe(session.recipeId);
  if (!recipe) return null;

  // Resume (PAUSED → COOKING) requires canCook; Pause (COOKING → PAUSED) requires canUpdate.
  const canPauseResume = session.status === "PAUSED" ? canCook : canUpdate;

  async function handlePauseResume() {
    const next: CookingSessionStatus = session.status === "PAUSED" ? "COOKING" : "PAUSED";
    await onAction(session.id, next);
  }
  async function handleComplete() {
    await onAction(session.id, "COMPLETED");
    onClose();
  }

  return (
    <CookingSessionDetailsPanel
      session={session}
      recipe={recipe as Recipe}
      open
      onOpenChange={(v) => { if (!v) onClose(); }}
      onPauseResume={canPauseResume ? handlePauseResume : undefined}
      onComplete={canUpdate ? handleComplete : undefined}
      onCancel={onClose}
      onCookMode={onCookMode}
    />
  );
}

interface CookingNowSectionProps {
  locationId?: number;
  source?: CookingSessionSource;
}

export function CookingNowSection({ locationId, source }: CookingNowSectionProps) {
  const { t } = useLingui();
  const { hasPermission } = useAuth();
  const canCook = hasPermission("cooking-session:cook");
  const canUpdate = hasPermission("cooking-session:update");
  const canDelete = hasPermission("cooking-session:delete");
  // On Prep the ticks are shared with Multi View (YC-23); on the service board they stay local.
  const [selected, setSelected] = useSelectionSlot("cookingNow", source === "prep");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("startedAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [assigneeFilter, setAssigneeFilter] = useState<AssigneeFilter>(null);
  const [showSort, setShowSort] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [cookModeSession, setCookModeSession] = useState<{ recipeId: number; sessionId: number; batchScale: number } | null>(null);
  const [detailSession, setDetailSession] = useState<CookingSession | null>(null);
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const [bulkConfirm, setBulkConfirm] = useState<{ ids: number[]; action: BulkSessionAction; inverseAction: BulkSessionAction | null } | null>(null);

  const SORT_OPTIONS = [
    { label: t`Start time (newest)`, sortBy: "startedAt",    sortOrder: "desc" as const },
    { label: t`Start time (oldest)`, sortBy: "startedAt",    sortOrder: "asc"  as const },
    { label: t`Recipe name`,         sortBy: "recipeName",   sortOrder: "asc"  as const },
    { label: t`Time remaining`,      sortBy: "targetMinutes", sortOrder: "asc" as const },
  ];

  const updateSession = useUpdateCookingSession();
  const deleteSession = useDeleteCookingSession();
  const bulkAction = useBulkActionCookingSessions();

  const statusParam =
    statusFilter === "all"     ? (["COOKING", "PAUSED"] as CookingSessionStatus[]) :
    statusFilter === "late"    ? (["COOKING"] as CookingSessionStatus[]) :
    ([statusFilter] as CookingSessionStatus[]);

  const { data, isLoading } = useCookingSessions({
    locationId,
    source,
    status: statusParam,
    search: search || undefined,
    sortBy,
    sortOrder,
    page,
    limit: PAGE_SIZE,
    ...assigneeFilterParams(assigneeFilter),
  });

  const sessions = data?.data ?? [];
  const displaySessions = statusFilter === "late" ? sessions.filter((s) => s.isLate) : sessions;
  const total = statusFilter === "late" ? displaySessions.length : (data?.meta.total ?? 0);
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const allSelected = displaySessions.length > 0 && displaySessions.every((r) => selected[r.id]);
  const someSelected = displaySessions.some((r) => selected[r.id]);
  const selectedIds = displaySessions.filter((r) => selected[r.id]).map((r) => r.id);

  function handleSearch(v: string) { setSearch(v); setPage(1); }
  function toggleAll(v: boolean) {
    setSelected(v ? Object.fromEntries(displaySessions.map((r) => [r.id, true])) : {});
  }
  async function handleAction(id: number, status: CookingSessionStatus): Promise<void> {
    try {
      await updateSession.mutateAsync({ id, data: { status } });
    } catch {
      // error surfaced via updateSession.isError
    }
  }
  async function handleDeleteSession(id: number): Promise<void> {
    try {
      await deleteSession.mutateAsync(id);
    } catch {
      // error surfaced via deleteSession.isError
    }
  }
  const COOKING_NOW_UNDO: Partial<Record<BulkSessionAction, BulkSessionAction>> = {
    COMPLETED: "COOKING",
    PAUSED:    "COOKING",
    COOKING:   "PAUSED",
  };

  async function handleBulkAction(action: BulkSessionAction): Promise<void> {
    const ids = [...selectedIds];

    // No completedAt to snapshot: since T-1108 a finished session is not in this
    // lane to be selected, so undoing here only ever puts live work back.
    try {
      await bulkAction.mutateAsync({ ids, action });
      setSelected({});
      setBulkConfirm({ ids, action, inverseAction: COOKING_NOW_UNDO[action] ?? null });
    } catch {
      // error surfaced via bulkAction.isError
    }
  }

  const handleBulkUndo = useCallback(async () => {
    if (!bulkConfirm?.inverseAction) return;
    try {
      await bulkAction.mutateAsync({ ids: bulkConfirm.ids, action: bulkConfirm.inverseAction });
      setBulkConfirm(null);
    } catch {
      // error surfaced via bulkAction.isError
    }
  }, [bulkConfirm, bulkAction]);

  return (
    <div className="flex flex-col px-3" data-testid="cooking-now-section">
      {/* Section title */}
      <div className="flex items-center justify-between py-3">
        <div className="flex items-center gap-2">
          <span className="text-lg font-semibold text-(--color-text-primary)">{t`Cooking now`}</span>
          <Badge variant="neutral" className="rounded-full text-xs">
            {isLoading ? "…" : t`${total} active`}
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => setViewMode("list")} className={cn("h-9 w-9", viewMode === "list" && "bg-(--color-surface-muted)")}><LayoutList className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" onClick={() => setViewMode("grid")} className={cn("h-9 w-9", viewMode === "grid" && "bg-(--color-surface-muted)")}><LayoutGrid className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between pb-3">
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-(--color-text-secondary)" />
          <Input placeholder={t`Search...`} value={search} onChange={(e) => handleSearch(e.target.value)} className="pl-9 h-11" />
        </div>
        <div className="flex items-center gap-2">
          {/* Sort dropdown */}
          <div className="relative">
            <Button variant="brand-outline" size="sm" className="h-11 gap-2" onClick={() => { setShowSort(!showSort); setShowFilter(false); }}>
              <ArrowUpDown className="h-4 w-4" />
              {t`Sort`}
            </Button>
            {showSort && (
              <div className="absolute right-0 top-12 z-10 w-52 rounded-lg border border-(--color-border) bg-(--color-surface-page) shadow-md">
                {SORT_OPTIONS.map((opt) => (
                  <Button key={opt.label} variant="ghost"
                    onClick={() => { setSortBy(opt.sortBy); setSortOrder(opt.sortOrder); setShowSort(false); }}
                    className={cn("w-full justify-start px-3 py-2.5 h-auto text-sm",
                      sortBy === opt.sortBy && sortOrder === opt.sortOrder && "font-medium text-(--color-action)")}>
                    {opt.label}
                  </Button>
                ))}
              </div>
            )}
          </div>

          {/* Filter dropdown */}
          <div className="relative">
            <Button variant="brand-outline" size="sm" className="h-11 gap-2" onClick={() => { setShowFilter(!showFilter); setShowSort(false); }}>
              <SlidersHorizontal className="h-4 w-4" />
              {t`Filter`}
              {(statusFilter !== "all" || assigneeFilter !== null) && <span className="ml-1 size-2 rounded-full bg-(--color-action)" />}
            </Button>
            {showFilter && (
              <div className="absolute right-0 top-12 z-10 w-48 max-h-96 overflow-y-auto rounded-lg border border-(--color-border) bg-(--color-surface-page) shadow-md p-2">
                <p className="px-2 pb-1 text-xs font-medium text-(--color-text-secondary) uppercase tracking-wide">{t`Status`}</p>
                {(["all", "COOKING", "PAUSED", "late"] as StatusFilter[]).map((s) => (
                  <Button key={s} variant="ghost"
                    onClick={() => { setStatusFilter(s); setShowFilter(false); setPage(1); }}
                    className={cn("w-full justify-start px-2 py-2 h-auto text-sm",
                      statusFilter === s && "font-medium text-(--color-action)")}>
                    {s === "all" ? t`All` : s === "late" ? t`Late` : s === "COOKING" ? t`Cooking` : t`Paused`}
                  </Button>
                ))}
                <AssigneeFilterOptions
                  locationId={locationId}
                  value={assigneeFilter}
                  onChange={(next) => { setAssigneeFilter(next); setShowFilter(false); setPage(1); }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bulk actions — fixed floating bar at bottom-center */}
      <BulkActionsBar
        count={selectedIds.length}
        availableActions={[
          ...(canUpdate ? ["COMPLETED" as const, "PAUSED" as const] : []),
          ...(canCook ? ["COOKING" as const] : []),
          ...(canDelete ? ["DELETE" as const] : []),
        ]}
        onAction={handleBulkAction}
      />
      {bulkConfirm && (
        <BulkActionConfirmBar
          action={bulkConfirm.action}
          onUndo={bulkConfirm.inverseAction ? handleBulkUndo : undefined}
          onDismiss={() => setBulkConfirm(null)}
        />
      )}

      {/* Content */}
      {viewMode === "list" ? (
        <div className="@container overflow-x-auto rounded-xl border border-(--color-border)">
          {/* min-width must cover the full column set of the active tier (narrow ~685px,
              compact ~890px, wide ~1176px) so the shrink-0 columns scroll inside this
              container instead of spilling out */}
          <div className="min-w-171 @min-[56rem]:min-w-223 @min-[73.5rem]:min-w-294">
            <TableHeader allSelected={allSelected} someSelected={someSelected} onToggleAll={toggleAll} />
            <div className="p-2">
              {isLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-4 mb-1.5 bg-(--color-surface-muted) rounded-xl">
                    <Skeleton className="h-8 w-8 rounded" />
                    <Skeleton className="h-4 flex-1" />
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                ))
              ) : displaySessions.length === 0 ? (
                <div className="py-12 flex flex-col items-center gap-2 text-(--color-text-secondary)">
                  <ChefHat className="h-8 w-8" />
                  <p className="text-sm">{t`No active sessions.`}</p>
                </div>
              ) : (
                displaySessions.map((row) => (
                  <CookingRow
                    key={row.id}
                    row={row}
                    selected={!!selected[row.id]}
                    onSelect={(v) => setSelected((s) => ({ ...s, [row.id]: v }))}
                    onAction={handleAction}
                    onDelete={handleDeleteSession}
                    onViewDetails={() => setDetailSession(row)}
                    onCookMode={() => setCookModeSession({ recipeId: row.recipeId, sessionId: row.id, batchScale: row.batchScale })}
                    canCook={canCook}
                    canUpdate={canUpdate}
                    canDelete={canDelete}
                  />
                ))
              )}
            </div>
            <div className="flex flex-col border-t border-(--color-border)">
              <div className="px-5 py-2">
                <span className="text-xs text-(--color-text-secondary)">
                  {t`${Object.values(selected).filter(Boolean).length} of ${total} row(s) selected.`}
                </span>
              </div>
              <WorkingTablePagination page={page} pageCount={pageCount} onPage={setPage} />
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {isLoading ? (
            <div className="grid grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-(--color-surface-muted) rounded-2xl p-3 flex flex-col gap-4">
                  <div className="flex gap-3 items-start">
                    <Skeleton className="size-17 rounded-full shrink-0" />
                    <div className="flex flex-col gap-2 flex-1">
                      <Skeleton className="h-6 w-32" />
                      <Skeleton className="h-5 w-40" />
                      <Skeleton className="h-4 w-20" />
                    </div>
                  </div>
                  <Skeleton className="h-10 w-full rounded-xl" />
                  <Skeleton className="h-10 w-full rounded-xl" />
                  <Skeleton className="h-11 w-full rounded-xl" />
                </div>
              ))}
            </div>
          ) : displaySessions.length === 0 ? (
            <div className="py-12 flex flex-col items-center gap-2 text-(--color-text-secondary)">
              <ChefHat className="h-8 w-8" />
              <p className="text-sm">{t`No active sessions.`}</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4">
              {displaySessions.map((row) => (
                <CookingNowCard
                  key={row.id}
                  row={row}
                  selected={!!selected[row.id]}
                  onSelect={(v) => setSelected((s) => ({ ...s, [row.id]: v }))}
                  onAction={handleAction}
                  onDelete={handleDeleteSession}
                  onViewDetails={() => setDetailSession(row)}
                  onCookMode={() => setCookModeSession({ recipeId: row.recipeId, sessionId: row.id, batchScale: row.batchScale })}
                  canCook={canCook}
                  canUpdate={canUpdate}
                  canDelete={canDelete}
                />
              ))}
            </div>
          )}
          <div className="flex items-center justify-between pt-2">
            <span className="text-xs text-(--color-text-secondary)">
              {t`${Object.values(selected).filter(Boolean).length} of ${total} row(s) selected.`}
            </span>
            <WorkingTablePagination page={page} pageCount={pageCount} onPage={setPage} />
          </div>
        </div>
      )}

      {/* Cook mode overlay (kept for direct-launch from dropdown) */}
      {cookModeSession !== null && (
        <CookModeLoader
          recipeId={cookModeSession.recipeId}
          sessionId={cookModeSession.sessionId}
          batchScale={cookModeSession.batchScale}
          onClose={() => setCookModeSession(null)}
        />
      )}

      {/* Session details panel */}
      {detailSession !== null && (
        <DetailsPanelLoader
          session={detailSession}
          onClose={() => setDetailSession(null)}
          onAction={handleAction}
          canCook={canCook}
          canUpdate={canUpdate}
          onCookMode={canCook ? () => {
            const s = detailSession;
            setDetailSession(null);
            setCookModeSession({ recipeId: s.recipeId, sessionId: s.id, batchScale: s.batchScale });
          } : undefined}
        />
      )}
    </div>
  );
}
